import { useHandleResponse } from '@/utils/useHandleResponse';
import {
  CheckOutlined,
  CloseOutlined,
  DownloadOutlined,
  DownOutlined,
  FilePdfOutlined,
  FileSearchOutlined,
  LoadingOutlined,
  PlayCircleOutlined,
  SendOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  App,
  Badge,
  Button,
  Col,
  Dropdown,
  Input,
  Modal,
  Radio,
  Row,
  Select,
  Spin,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Upload,
} from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import { debounce } from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth } from '../../../../context/AuthProvider';
import { updateProps } from '../../../../contextRedux/InterventionFile/InterventionFileModel';
import CompanyService from '../../../../services/company/CompanyService';
import InterventionFileService from '../../../../services/interventionFile/InterventionFileService';
import SystemTypeService from '../../../../services/systemType/SystemTypeService';
import { RootState } from '../../../../store';
import request from '../../../../utils/request';
import { saveBlobCrossPlatform } from '../../../../utils/saveBlobCrossPlatform';

const InterventionFileRecommendations: React.FC = () => {
  const dispatch = useDispatch();
  const { modal, message } = App.useApp();
  const handleResponse = useHandleResponse();

  const searchTerm = useSelector((state: RootState) => state.models?.interventionFile.props.searchTerm);
  const searchTermType = useSelector((state: RootState) => state.models?.interventionFile.props.searchTermType);

  const [uploadVisible, setUploadVisible] = useState(false);
  const [currentRecord, setCurrentRecord] = useState<any | null>(null);
  const [interventionFiles, setInterventionFiles] = useState<any[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  const [fileList, setFileList] = useState<any[]>([]);
  const [isOfferUpload, setIsOfferUpload] = useState(false);
  const [existingFile, setExistingFile] = useState<any | null>(null);

  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [selectedWorkPlace, setSelectedWorkPlace] = useState<any>(null);
  const [selectedSystemType, setSelectedSystemType] = useState<any>(null);
  const [pdfViewerVisible, setPdfViewerVisible] = useState<boolean>(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [pdfFilename, setPdfFilename] = useState<string>('file.pdf');

  const [activeKey, setActiveKey] = useState('Noi venite');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [tabTotals, setTabTotals] = useState<Record<string, number>>({});
  const { profile } = useAuth();

  const statusIdsByTab: Record<string, number[]> = useMemo(
    () => ({
      'Noi venite': [1],
      'In curs': [2],
      'In Ofertare': [5, 6],
      'Neofertate/Refuzate': [8, 7],
      Acceptate: [3],
      Finalizate: [4],
    }),
    []
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [activeKey, searchTerm, searchTermType, selectedCompany, selectedWorkPlace, selectedSystemType]);

  const { data: countsData } = useQuery({
    queryKey: [
      'interventionFilesCounts',
      searchTerm,
      searchTermType,
      selectedCompany,
      selectedWorkPlace,
      selectedSystemType,
    ],
    queryFn: () =>
      InterventionFileService.searchCounts({
        hasRecommendationDetails: true,
        termen: searchTerm || undefined,
        termenType: searchTermType || undefined,
        companyName: selectedCompany || undefined,
        workPlaceName: selectedWorkPlace || undefined,
        systemTypeName: selectedSystemType || undefined,
      }),
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    const counts = countsData?.response ?? null;
    if (!counts) {
      return;
    }
    const getCount = (id: number) => (counts as any)[id] ?? (counts as any)[String(id)] ?? 0;
    setTabTotals({
      'Noi venite': getCount(1),
      'In curs': getCount(2),
      'In Ofertare': getCount(5) + getCount(6),
      'Neofertate/Refuzate': getCount(8) + getCount(7),
      Acceptate: getCount(3),
      Finalizate: getCount(4),
    });
  }, [countsData]);

  const {
    data: _data,
    isFetching: isInterventionFilesLoading,
    refetch,
  } = useQuery({
    queryKey: [
      'interventionFiles',
      activeKey,
      currentPage,
      pageSize,
      searchTerm,
      searchTermType,
      selectedCompany,
      selectedWorkPlace,
      selectedSystemType,
    ],
    queryFn: () =>
      InterventionFileService.search({
        isInterventionFileSystemTypeIncluded: true,
        isInterventionFileInvoiceFilesIncluded: true,
        isInterventionFileEngineerIncluded: true,
        isInterventionFilePerformOperationsIncluded: true,
        isInterventionFileEquipmentsIncluded: true,
        isMentenancePlanningIncluded: true,
        isCompanyIncluded: true,
        isWorkPlaceIncluded: true,
        isAgentIncluded: true,
        isStatusIncluded: true,
        hasRecommendationDetails: true,
        statusIds: statusIdsByTab[activeKey],
        skip: (currentPage - 1) * pageSize,
        take: pageSize,
        termen: searchTerm || undefined,
        termenType: searchTermType || undefined,
        companyName: selectedCompany || undefined,
        workPlaceName: selectedWorkPlace || undefined,
        systemTypeName: selectedSystemType || undefined,
      }).then((data) => {
        const response = data?.response?.filter((item: any) => item.isDraft != true) ?? [];
        setInterventionFiles(response);
        const total = data?.totalItems ?? response.length;
        setTotalItems(total);
        setTabTotals((prev) => ({ ...prev, [activeKey]: total }));
        return data?.response;
      }),
    enabled: !!activeKey,
    refetchOnWindowFocus: false,
  });
  const { data: companies, isFetching: isCompaniesLoading } = useQuery({
    queryKey: ['companies'],
    queryFn: () =>
      CompanyService.search({
        isAgentIncluded: true,
        isWorkPlacesIncluded: true,
        isAccountManagerIncluded: true,
        isActive: true,
      }).then((data) => {
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: systemTypes, isFetching: isSystemTypesLoading } = useQuery({
    queryKey: ['systemTypes'],
    queryFn: () =>
      SystemTypeService.search({
        isDeviceTypeIncluded: true,
        isActive: true,
      }).then((data) => {
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const workPlaces = useMemo(() => {
    if (!selectedCompany) return [];
    const company = companies?.find((c: any) => c.name === selectedCompany);
    return company?.workPlaces || [];
  }, [companies, selectedCompany]);

  const { mutate: donwloadFile, isPending: isDownloadInvoiceFile } = useMutation({
    mutationFn: (formData: any) => InterventionFileService.downloadFile(formData),
    onSuccess: (response: any) => {
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'file.pdf';
      if (contentDisposition) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }
      return response.blob().then((blob: any) => {
        saveBlobCrossPlatform(blob, filename);
      });
    },
    onError: (error: any) => {
      message.error('Eroare procesare request: ' + error.message);
    },
  });
  const { mutate: saveInterventionFile, isPending: isSavingInterventionFile } = useMutation({
    mutationFn: (formData: any) => InterventionFileService.upsert(formData.id, formData),
    onSuccess: (data: any) => {
      if (!data) {
        console.error('Received data is not in JSON format or JSON parsing failed.');
        return;
      }
      handleResponse(data, data?.response?.number.toString());
      refetch();
    },
    onError: (error: any) => {
      message.error(`Error processing your request: ${error.message}`);
    },
  });
  const sanitizeFilename = (value: string) =>
    value
      .replace(/[\\/:*?"<>|]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const buildPdfFilename = (record: any) => {
    const companyName = record?.metadata?.company?.name ?? record?.company?.name ?? 'Client';
    const workPlaceName = record?.metadata?.workPlace?.name ?? record?.workPlace?.name ?? 'Workplace';
    const fileNumber = record?.number ?? record?.metadata?.number ?? '';
    const base =
      !companyName || !workPlaceName || !fileNumber
        ? record?.id ?? 'file'
        : `${companyName} ${workPlaceName} ${fileNumber}`;
    return `${sanitizeFilename(base)}.pdf`;
  };

  const { mutate: printInterventionFile, isPending: isPrinting } = useMutation<
    Response, // Type of the success response
    Error, // Type of the error
    { record: any; action: 'view' | 'download' | 'newTab'; targetWindow?: Window | null } // Type of the variables passed to mutate
  >({
    mutationFn: async ({ record }) => {
      // Always fetch the file with authentication headers
      const response = await InterventionFileService.print(record.id);
      return response;
    },
    onSuccess: async (response, { action, targetWindow, record }) => {
      if (!response.ok) {
        throw new Error(`Network response was not ok: ${response.statusText}`);
      }
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'file.pdf';

      if (contentDisposition) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filename = decodeURIComponent(matches[1].replace(/['"]/g, ''));
        }
      }

      const blob = await response.blob();
      if (action === 'newTab' || action === 'view') {
        const blobUrl = window.URL.createObjectURL(blob);
        const pdfFilename = buildPdfFilename(record);
        const escapedFilename = pdfFilename.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
        const htmlContent = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>PDF - ${escapedFilename}</title></head><body style="margin:0;padding:0;overflow:hidden;"><iframe src="${blobUrl}" style="width:100%;height:100vh;border:none;"></iframe><div style="position:fixed;top:10px;right:10px;z-index:9999;background:rgba(255,255,255,0.95);padding:8px 12px;border-radius:4px;box-shadow:0 2px 8px rgba(0,0,0,0.15);"><a href="${blobUrl}" download="${escapedFilename}" style="text-decoration:none;color:#1890ff;font-weight:500;">Descarcă PDF</a></div></body></html>`;
        const htmlBlob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
        const htmlBlobUrl = window.URL.createObjectURL(htmlBlob);
        if (targetWindow && !targetWindow.closed) {
          targetWindow.location.href = htmlBlobUrl;
        } else {
          window.open(htmlBlobUrl, '_blank', 'noopener,noreferrer');
        }
        setTimeout(() => window.URL.revokeObjectURL(htmlBlobUrl), 60_000);
        return;
      }

      if (action === 'download') {
        saveBlobCrossPlatform(blob, buildPdfFilename(record));
      }
    },
    onError: (error) => {
      console.error('Error processing your request:', error);
      message.error('Eroare la încărcarea fișierului PDF.');
    },
  });
  const openPdfInModal = (record: any) => {
    const w = window as any;
    const isApkMode = w.AndroidInterface && typeof w.AndroidInterface.saveBlobFile === 'function';
    if (isApkMode) {
      printInterventionFile({ record, action: 'download' });
      return;
    }
    const targetWindow = window.open('', '_blank');
    if (!targetWindow) {
      message.warning('Permiteți ferestrele pop-up pentru a deschide PDF-ul într-un tab nou.');
      return;
    }
    try {
      targetWindow.opener = null;
    } catch {}
    printInterventionFile({ record, action: 'newTab', targetWindow });
  };
  const preview = (record: any) => {
    const w = window as any;
    const isApkMode = w.AndroidInterface && typeof w.AndroidInterface.saveBlobFile === 'function';
    if (isApkMode) {
      printInterventionFile({ record, action: 'download' });
      return;
    }
    if (!record?.id) {
      message.error('ID-ul fișei de intervenție lipsește.');
      return;
    }
    const targetWindow = window.open('', '_blank');
    if (!targetWindow) {
      message.warning('Permiteți ferestrele pop-up pentru a deschide PDF-ul într-un tab nou.');
      return;
    }
    try {
      targetWindow.opener = null;
    } catch {}
    printInterventionFile({ record, action: 'newTab', targetWindow });
  };

  // Handle PDF viewer modal close
  const handlePdfViewerClose = () => {
    if (pdfBlobUrl) {
      window.URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
    setPdfViewerVisible(false);
    setPdfFilename('file.pdf');
  };

  // Handle download from PDF viewer
  const handleDownloadFromViewer = () => {
    if (pdfBlobUrl) {
      fetch(pdfBlobUrl)
        .then((res) => res.blob())
        .then((blob) => {
          saveBlobCrossPlatform(blob, pdfFilename);
        })
        .catch((error) => {
          console.error('Error downloading PDF:', error);
          message.error('Eroare la descărcarea fișierului PDF.');
        });
    }
  };
  const uploadFiles = async (files: File[]): Promise<any[]> => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    try {
      const response = await request(
        `/interventionFile/upload`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            Authorization: 'Bearer ' + localStorage.getItem('authToken'),
          },
          body: formData,
        },
        true
      );
      const data = await response.json();
      if (data?.errors?.length > 0) {
        message.error(data?.errors[0]);
        return [];
      } else {
        message.success(`Fisierul a fost incarcat cu success!`);
        return data?.response;
      }
    } catch (error: any) {
      message.error(`Incarcare nereusita: ${error.message}`);
      return [];
    }
  };
  const handleDownloadFile = async (file: any) => {
    const interventionFile = interventionFiles.find((f: any) => f.id == file.interventionFileId);
    let matchingFile = interventionFile.interventionFileInvoiceFiles.find((i: any) => i.fileName === file.fileName);

    if (matchingFile) {
      const formData = new FormData();
      formData.append('fileName', matchingFile.fileName);
      formData.append('fileData', matchingFile.filePath);
      formData.append('fileType', matchingFile.fileExtension);
      donwloadFile(formData);
    } else {
      message.error('File not found.');
    }
  };
  const handleStatusChange = (record: any, offerStatus?: any) => {
    if (record.statusId === 1) {
      // Moving from "Noi venite" to "In curs"
      record.statusId = 2;
      record.changes = [...(record?.changes || []), `[${profile?.name}] Procesata ${new Date().toLocaleString()}`];
      saveInterventionFile(record);
      return null;
    } else if (record.statusId === 2) {
      // Moving from "In curs" to "In Ofertare" with offer upload
      setIsOfferUpload(true);
      setUploadedFiles([]);
      setFileList([]);
      setCurrentRecord(record);
      setExistingFile(record.interventionFileInvoiceFiles?.[0] || null);
      setUploadVisible(true);
      return null;
    } else if (record.statusId === 3) {
      // Moving from "Acceptate" to "Finalizate" - requires file upload
      setUploadedFiles([]);
      setFileList([]);
      setCurrentRecord(record);
      setExistingFile(record.interventionFileInvoiceFiles?.[0] || null);
      setUploadVisible(true);
      return null;
    } else if (record.statusId === 7) {
      // Moving from "Neofertata" to "Finalizate" - requires file upload
      setUploadedFiles([]);
      setFileList([]);
      setCurrentRecord(record);
      setExistingFile(record.interventionFileInvoiceFiles?.[0] || null);
      setUploadVisible(true);
      return null;
    } else if (record.statusId === 5 || record.statusId === 6 || record.statusId === 7) {
      // Handling offer status changes
      switch (offerStatus) {
        case 'acceptata':
          let selectedOption = '';
          modal.confirm({
            title: 'Selecteaza metoda de acceptare',
            content: (
              <Radio.Group onChange={(e) => (selectedOption = e.target.value)}>
                <Radio value='Acceptat SICAP'>Acceptat SICAP</Radio>
                <Radio value='Acceptat Direct'>Acceptat Direct</Radio>
              </Radio.Group>
            ),
            onOk: () => {
              if (!selectedOption) {
                message.error('Va rugam sa selectati o optiune!');
                return;
              }
              const updatedRecord = {
                ...record,
                statusId: 3, // Move to "Acceptate"
                status: { name: 'Acceptata' },
                changes: [
                  ...(record?.changes || []),
                  `[${profile?.name}] Oferta acceptata (${selectedOption}) - ${new Date().toLocaleString()}`,
                ],
              };
              saveInterventionFile(updatedRecord);
            },
          });
          break;
        case 'refuzata':
          record.statusId = 7;
          record.changes = [
            ...(record?.changes || []),
            `[${profile?.name}] Oferta refuzata ${new Date().toLocaleString()}`,
          ];
          saveInterventionFile(record);
          break;
        case 'in renegociere':
          record.statusId = 6;
          record.changes = [
            ...(record?.changes || []),
            `[${profile?.name}] Oferta in renegociere ${new Date().toLocaleString()}`,
          ];
          saveInterventionFile(record);
          break;
      }
      return null;
    } else if (record.statusId === 4) {
      // Display Invoice Status
      return record.isInvoiceSent ? (
        <Tag color='blue'>Factura Trimisa</Tag>
      ) : (
        <Tag color='purple'>Factura Netrimisa</Tag>
      );
    }
    return null;
  };
  const handleEditOffer = (offerId: any) => {
    window.open(`/project-management/pmOffer/edit?id=${offerId}`, '_blank');
  };
  const handleDeleteFile = () => {
    setExistingFile(null);
    message.success('Fisier sters cu succes.');
  };
  const handleUploadOk = async () => {
    if (uploadedFiles.length === 0 && !existingFile) {
      message.error('Va rog sa incarcati un fisier.');
      return;
    }

    const fileToSave = uploadedFiles[0] || existingFile;

    if (isOfferUpload) {
      // Handle offer file upload
      const updatedRecord = {
        ...currentRecord,
        statusId: 5, // Move to "In Ofertare"
        status: { name: 'In Ofertare' },
        interventionFileInvoiceFiles: fileToSave ? [fileToSave] : [],
        changes: [
          ...(currentRecord?.changes || []),
          `[${profile?.name}] Oferta incarcata (${fileToSave?.fileName}) - ${new Date().toLocaleString()}`,
        ],
      };
      await saveInterventionFile(updatedRecord);
    } else {
      // Handle finalization file upload
      const updatedRecord = {
        ...currentRecord,
        interventionFileInvoiceFiles: fileToSave ? [fileToSave] : [],
        statusId: 4, // Move to Finalizate
        status: { name: 'Finalizata' },
        changes: [
          ...(currentRecord?.changes || []),
          `[${profile?.name}] Fisiere incarcate (${fileToSave?.fileName}) - ${new Date().toLocaleString()}`,
        ],
      };
      await saveInterventionFile(updatedRecord);
    }

    setUploadedFiles([]);
    setFileList([]);
    setCurrentRecord(null);
    setUploadVisible(false);
    setIsOfferUpload(false);
    setExistingFile(null);
  };
  const handleCancel = () => {
    setUploadedFiles([]);
    setFileList([]);
    setCurrentRecord(null);
    setUploadVisible(false);
    setIsOfferUpload(false);
    setExistingFile(null);
  };
  const handleInvoiceToggle = (record: any, checked: boolean) => {
    const updatedRecord = {
      ...record,
      isInvoiceSent: checked,
      changes: [
        ...record.changes,
        `[${profile?.name}] Factura ${checked == true ? 'trimisa' : 'netrimisa'} - ${new Date().toLocaleString()}`,
      ],
    };
    const updatedRecords = interventionFiles.map((file) => (file.id === record.id ? updatedRecord : file));
    setInterventionFiles(updatedRecords);
    saveInterventionFile(updatedRecord);
  };
  const handleViewChanges = (record: any) => {
    modal.info({
      title: `Modificari si fisiere pentru fisa cu numarul: ${record.number}`,
      content: (
        <div>
          <h3>Modificari:</h3>
          {record?.changes?.length > 0 ? (
            record.changes.map((change: any, index: any) => (
              <p key={index}>
                {index + 1}. {change}
              </p>
            ))
          ) : (
            <p>Inca nu au fost facute modificari.</p>
          )}
          <h3>Fisiere Incarcate:</h3>
          {record?.interventionFileInvoiceFiles?.length > 0 ? (
            record.interventionFileInvoiceFiles.map((file: any, index: number) => (
              <Button
                key={file?.id || index}
                type='link'
                icon={<DownloadOutlined />}
                onClick={() => handleDownloadFile(file)}
              >
                {file?.fileName}
              </Button>
            ))
          ) : (
            <p>Niciun fisier incarcat.</p>
          )}
        </div>
      ),
    });
  };

  const handleCompanyChange = (value: string | null) => {
    setSelectedCompany(value);
    dispatch(updateProps({ field: 'skip', value: 0 }));
  };

  const handleWorkPlaceChange = (value: string | null) => {
    setSelectedWorkPlace(value);
    dispatch(updateProps({ field: 'skip', value: 0 }));
  };

  const handleSystemTypeChange = (value: string | null) => {
    setSelectedSystemType(value);
    dispatch(updateProps({ field: 'skip', value: 0 }));
  };

  const handleSearch = useCallback(
    (value: string) => {
      debouncedSearch(value, 'searchTerm');
    },
    [searchTerm]
  );

  const handleTypeChange = useCallback(
    (value: string) => {
      debouncedSearch(value, 'searchTermType');
    },
    [searchTermType]
  );
  const debouncedSearch = useCallback(
    debounce((value: string, search: string) => {
      dispatch(updateProps({ field: search, value: value }));
      dispatch(updateProps({ field: 'skip', value: 0 }));
    }, 1000),
    []
  );

  const uploadProps: any = {
    multiple: false,
    showUploadList: true,
    fileList,
    customRequest: async ({ onSuccess, onError, file }: any) => {
      try {
        const uploadedFile = await uploadFiles([file]);
        if (uploadedFile.length > 0) {
          const newFile = {
            id: '',
            interventionFileId: currentRecord.id,
            fileName: file.name,
            fileExtension: file.type,
            fileContent: uploadedFile,
            filePath: uploadedFile,
          };
          setUploadedFiles([newFile]);
          setFileList([file]);
          onSuccess('ok');
        } else {
          onError('File upload failed');
        }
      } catch (error) {
        console.error('Upload error:', error);
        onError(error);
      }
    },
    beforeUpload: (file: any) => {
      const isAllowedType = [
        'image/jpg',
        'image/jpeg',
        'image/png',
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ].includes(file.type);
      if (!isAllowedType) {
        message.error('You can only upload JPG/PNG images or PDF/DOC files!');
        return Upload.LIST_IGNORE;
      }
      return true;
    },
    itemRender: (_originNode: React.ReactNode, file: UploadFile) => {
      return (
        <div className='file-actions'>
          <div className='download-action-button'>
            <Button
              type='link'
              icon={<DownloadOutlined />}
              onClick={() => handleDownloadFile(file)}
              disabled={currentRecord.statusId === 3}
            >
              {file.name}
            </Button>
          </div>
          <div className='delete-action-button'>
            <Button
              type='link'
              danger
              onClick={() => {
                setUploadedFiles([]);
                setFileList([]);
                message.success(`File "${file.name}" removed successfully.`);
              }}
            >
              Sterge
            </Button>
          </div>
        </div>
      );
    },
  };
  const columns: any = useMemo(() => {
    const baseColumns = [
      {
        title: 'Numar',
        dataIndex: '',
        key: '',
        render: (record: any) => (
          <Tag color={'blue'} style={{ cursor: 'pointer' }} onClick={() => preview(record)}>
            {record?.number}
          </Tag>
        ),
      },
      {
        title: 'Tip',
        dataIndex: 'type',
        key: 'type',
      },
      {
        title: 'Firma',
        key: 'company',
        render: (record: any) => {
          const company = record?.metadata?.company ?? record?.company;
          return (
            <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: 200 }}>
              {company?.name?.length > 15 ? company.name : company?.name}
            </div>
          );
        },
      },
      {
        title: 'Locatie',
        dataIndex: '',
        key: 'workPlace',
        render: (record: any) => (
          <>
            <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: 300 }}>
              <b>
                {record?.metadata?.workPlace?.name?.length ?? record?.workPlace?.name?.length > 30
                  ? record?.metadata?.workPlace?.name ?? record?.workPlace?.name
                  : record?.metadata?.workPlace?.name ?? record?.workPlace?.name}
              </b>
            </div>
            <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: 300 }}>
              {record?.metadata?.address?.length ?? record?.address?.length > 30
                ? record?.metadata?.workPlace?.address ?? record?.workPlace?.address
                : record?.metadata?.workPlace?.address ?? record?.workPlace?.address}
            </div>
          </>
        ),
      },
      {
        title: 'Periodicitate',
        dataIndex: '',
        key: '',
        render: (interventionFile: any) => (
          <>
            <div style={{ textAlign: 'center' }}>
              <b>
                {interventionFile?.mentenancePlanning != undefined
                  ? interventionFile?.mentenancePlanning?.periodicity
                  : interventionFile?.requestFileId != undefined
                  ? 'solicitare'
                  : ' - '}
              </b>
            </div>
          </>
        ),
      },
      {
        title: 'Sistem',
        dataIndex: 'interventionFileSystemTypes',
        key: 'interventionFileSystemTypes',
        render: (interventionFileSystemType: any[]) => (
          <>
            {interventionFileSystemType && interventionFileSystemType.length > 0 && (
              <span>
                {interventionFileSystemType.length > 0
                  ? interventionFileSystemType?.map((type: any) => `[${type.systemType.name}]`).join('')
                  : ''}
              </span>
            )}
          </>
        ),
      },
      {
        title: 'Tehnician',
        dataIndex: 'helionEngineers',
        key: 'helionEngineers',
        render: (helionEngineers: string[]) => (
          <>
            {helionEngineers?.length > 0 ? (
              helionEngineers?.map((engineer: any, index: any) => {
                return <div key={index}>{'[' + engineer?.user?.name + ']'}</div>;
              })
            ) : (
              <div>----</div>
            )}
          </>
        ),
      },
      {
        title: 'Data Semnare',
        dataIndex: 'deliveryDate',
        key: 'deliveryDate',
        render: (deliveryDate: string) => moment(deliveryDate).format('YYYY-MM-DD'),
      },
      {
        title: 'Recomandari',
        dataIndex: 'recommendationDetails',
        key: 'recommendationDetails',
        render: (text: any) => (
          <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: 300 }}>
            {text?.length > 30 ? text : text}
          </div>
        ),
      },
      {
        title: 'Oferta',
        dataIndex: 'offerId',
        key: 'offerId',
        render: (offerId: any) =>
          offerId ? (
            <Button onClick={() => handleEditOffer(offerId)}>
              <FileSearchOutlined />
            </Button>
          ) : (
            '-'
          ),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (status: any['status']) => <Tag>{status?.name}</Tag>,
      },
      {
        title: 'Actiuni',
        key: 'actions',
        render: (_: any, record: any) => {
          // Handling statusId 1: "Noi venite"
          if (record.statusId === 1) {
            const actionMenuItems = [
              {
                key: '1',
                label: (
                  <Button onClick={() => handleStatusChange(record)} style={{ width: '100%' }}>
                    Proceseaza <PlayCircleOutlined />
                  </Button>
                ),
              },
              {
                key: '2',
                label: (
                  <Button
                    onClick={() => {
                      record.statusId = 7;
                      record.changes = [
                        ...(record?.changes || []),
                        `[${profile?.name}] Neofertata ${new Date().toLocaleString()}`,
                      ];
                      saveInterventionFile(record);
                    }}
                    style={{ width: '100%' }}
                  >
                    Neofertata <CloseOutlined />
                  </Button>
                ),
              },
            ];
            return (
              <Dropdown placement='bottom' trigger={['click']} menu={{ items: actionMenuItems }}>
                <Button type='primary'>
                  Actiuni <DownOutlined />
                </Button>
              </Dropdown>
            );
          }
          // Handling statusId 2: "In curs"
          if (record.statusId === 2) {
            const actionMenuItems = [
              {
                key: '1',
                label: (
                  <Button
                    onClick={() =>
                      window.open(
                        `/project-management/pmOffer/new?interventionFileId=${record?.id}&companyId=${record?.companyId}`,
                        '_blank'
                      )
                    }
                    style={{ width: '100%' }}
                  >
                    Creeaza Oferta <SendOutlined />
                  </Button>
                ),
              },
              {
                key: '2',
                label: (
                  <Button onClick={() => handleStatusChange(record)} style={{ width: '100%' }}>
                    Am Oferta <SendOutlined />
                  </Button>
                ),
              },
              {
                key: '3',
                label: (
                  <Button
                    onClick={() => {
                      record.statusId = 7;
                      record.changes = [
                        ...(record?.changes || []),
                        `[${profile?.name}] Neofertata ${new Date().toLocaleString()}`,
                      ];
                      saveInterventionFile(record);
                    }}
                    style={{ width: '100%' }}
                  >
                    Neofertata <CloseOutlined />
                  </Button>
                ),
              },
            ];
            return (
              <Dropdown placement='bottom' trigger={['click']} menu={{ items: actionMenuItems }}>
                <Button type='primary'>
                  Actiuni <DownOutlined />
                </Button>
              </Dropdown>
            );
          }
          // Handling statusId 3: "Acceptate" -> "Finalizate"
          if (record.statusId === 3) {
            return (
              <Button onClick={() => handleStatusChange(record)} style={{ width: '100%' }}>
                Finalizeaza <CheckOutlined />
              </Button>
            );
          }
          // Handling statusId 4: Display Invoice Status
          // if (record.statusId === 4) {
          //   return record.isInvoiceSent ? (
          //     <Tag color='blue'>Factura Trimisa</Tag>
          //   ) : (
          //     <Tag color='purple'>Factura Netrimisa</Tag>
          //   );
          // }
          // Handling statusId 5,6,7: "In Ofertare" states
          if (record.statusId === 5 || record.statusId === 6) {
            const actionMenuItems: any = [
              {
                key: '1',
                label: (
                  <Button onClick={() => handleStatusChange(record, 'acceptata')} style={{ width: '100%' }}>
                    Acceptata <CheckOutlined />
                  </Button>
                ),
              },
              record.statusId !== 7 && {
                key: '2',
                label: (
                  <Button onClick={() => handleStatusChange(record, 'refuzata')} style={{ width: '100%' }}>
                    Refuzata <CloseOutlined />
                  </Button>
                ),
              },
              record.statusId !== 6 && {
                key: '3',
                label: (
                  <Button onClick={() => handleStatusChange(record, 'in renegociere')} style={{ width: '100%' }}>
                    Renegociere <LoadingOutlined />
                  </Button>
                ),
              },
              record.offerId && {
                key: '4',
                label: (
                  <Button onClick={() => handleEditOffer(record?.offerId)} style={{ width: '100%' }}>
                    Modifica Oferta <SendOutlined />
                  </Button>
                ),
              },
            ].filter(Boolean);
            return (
              <Dropdown placement='bottom' trigger={['click']} menu={{ items: actionMenuItems }}>
                <Button type='primary'>
                  Actiuni <DownOutlined />
                </Button>
              </Dropdown>
            );
          }

          return null;
        },
      },
      {
        title: 'Istoric modificari',
        key: 'changes',
        render: (_: any, record: any) => <Button onClick={() => handleViewChanges(record)}>Istoric</Button>,
      },
      {
        title: 'Vizualizare',
        key: 'viewPDF',
        render: (_: any, record: any) => {
          const previewButton = (
            <Tooltip title='Previzualizare PDF'>
              <Button icon={<FilePdfOutlined />} onClick={() => openPdfInModal(record)} />
            </Tooltip>
          );

          return <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>{previewButton}</div>;
        },
      },
    ];
    // Add 'Factura' column only if activeKey is 'Finalizate'
    if (activeKey === 'Finalizate') {
      baseColumns.push({
        title: 'Finalizata',
        key: 'isInvoiceSent',
        render: (_: any, record: any) =>
          record.statusId === 4 ? (
            <Switch
              checked={record.isInvoiceSent}
              disabled={record.isInvoiceSent}
              onChange={(checked) => handleInvoiceToggle(record, checked)}
            />
          ) : (
            <></>
          ),
      });
    }
    return baseColumns;
  }, [interventionFiles, activeKey]);

  if (
    isInterventionFilesLoading ||
    isSavingInterventionFile ||
    isDownloadInvoiceFile ||
    isCompaniesLoading ||
    isSystemTypesLoading ||
    isPrinting
  ) {
    return <Spin className='spinner' size='large' />;
  }

  const tabs = ['Noi venite', 'In curs', 'In Ofertare', 'Neofertate/Refuzate', 'Acceptate', 'Finalizate'];
  const tabItems: any = tabs.map((key) => ({
    key,
    label: (
      <>
        {key} <Badge count={tabTotals[key] ?? 0} color='blue' />
      </>
    ),
    children: (
      <div className='table-scroll-wrapper'>
        <Table
          dataSource={key === activeKey ? interventionFiles : []}
          columns={columns}
          rowKey='id'
          pagination={
            key === activeKey
              ? {
                  current: currentPage,
                  pageSize,
                  total: totalItems,
                  onChange: (page, size) => {
                    setCurrentPage(page);
                    setPageSize(size);
                  },
                  showSizeChanger: true,
                }
              : false
          }
          scroll={{ x: 'max-content' }}
        />
      </div>
    ),
  }));

  return (
    <div className='table-page-no-h-scroll'>
      <h1>Recomandari</h1>
      <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Input.Search
            placeholder='Search...'
            defaultValue={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            autoFocus={true}
            allowClear
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Select
            placeholder='Filtreaza dupa tip'
            value={searchTermType || undefined}
            onChange={handleTypeChange}
            style={{ width: '100%' }}
            allowClear
          >
            <Select.Option value='Service [cu contract]'>Service [cu contract]</Select.Option>
            <Select.Option value='Service [fara contract]'>Service [fara contract]</Select.Option>
            <Select.Option value='Garantie'>Garantie</Select.Option>
            <Select.Option value='Mentenanta periodica'>Mentenanta periodica</Select.Option>
          </Select>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Select
            placeholder='Filtreaza dupa beneficiar'
            value={selectedCompany}
            onChange={handleCompanyChange}
            style={{ width: '100%' }}
            allowClear
            showSearch
            optionFilterProp='children'
          >
            {companies.map((company: any) => (
              <Select.Option key={company?.id} value={company?.name}>
                {company?.name}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Select
            placeholder='Filtreaza dupa punct de lucru'
            value={selectedWorkPlace}
            onChange={handleWorkPlaceChange}
            style={{ width: '100%' }}
            allowClear
            showSearch
            optionFilterProp='children'
          >
            {workPlaces.map((workPlace: any) => (
              <Select.Option key={workPlace?.id} value={workPlace?.name}>
                {workPlace?.name}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Select
            placeholder='Filtreaza dupa sistem'
            value={selectedSystemType}
            onChange={handleSystemTypeChange}
            style={{ width: '100%' }}
            allowClear
            showSearch
            optionFilterProp='children'
          >
            {systemTypes.map((systemType: any) => (
              <Select.Option key={systemType?.id} value={systemType?.name}>
                {systemType?.name}
              </Select.Option>
            ))}
          </Select>
        </Col>
      </Row>
      <Tabs activeKey={activeKey} onChange={(key) => setActiveKey(key)} items={tabItems} />

      <Modal
        title={isOfferUpload ? 'Incarcati oferta' : 'Incarcati fisierele pentru finalizare'}
        open={uploadVisible}
        onOk={handleUploadOk}
        onCancel={handleCancel}
        width={800}
      >
        {!isOfferUpload && existingFile && (
          <div style={{ marginBottom: 20 }}>
            <h4>Fisier existent:</h4>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{existingFile.fileName}</span>
              <Button type='link' danger onClick={handleDeleteFile}>
                Sterge
              </Button>
            </div>
          </div>
        )}
        <Upload {...uploadProps}>
          <Button icon={<UploadOutlined />}>Selectati fisierul</Button>
        </Upload>
      </Modal>

      {/* PDF Viewer Modal */}
      <Modal
        title='Previzualizare PDF'
        open={pdfViewerVisible}
        onCancel={handlePdfViewerClose}
        footer={[
          <Button key='close' onClick={handlePdfViewerClose}>
            Închide
          </Button>,
          <Button key='download' type='primary' icon={<DownloadOutlined />} onClick={handleDownloadFromViewer}>
            Descarcă PDF
          </Button>,
        ]}
        width='90%'
        style={{ top: 20 }}
        bodyStyle={{ height: 'calc(100vh - 200px)', padding: 0 }}
        destroyOnClose
      >
        {pdfBlobUrl && (
          <iframe
            src={pdfBlobUrl}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
            }}
            title='PDF Viewer'
          />
        )}
      </Modal>
    </div>
  );
};
export default InterventionFileRecommendations;
